import { create } from 'zustand'
import { http } from '@/api/http'

export interface Profile {
  id: string
  email: string
  email_verified: boolean
  name?: string
  status: number
  created_at: string
}

interface SessionResp {
  session_id?: string
  profile?: Profile
}

interface AuthState {
  profile: Profile | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name?: string) => Promise<void>
  forgot: (email: string) => Promise<void>
  reset: (token: string, password: string) => Promise<void>
  logout: () => Promise<void>
  clearLocal: () => void
  fetchMe: () => Promise<void>
}

// the router guard keys on this localStorage entry; keep the flat
// {profile, csrf} shape the old pinia store persisted
const STORAGE_KEY = 'licensor-portal-auth'

function loadPersisted(): { profile: Profile | null; csrf: string } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : { profile: null, csrf: '' }
  } catch {
    return { profile: null, csrf: '' }
  }
}

function persist(state: { profile: Profile | null; csrf: string }) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ profile: state.profile, csrf: state.csrf }))
}

export const useAuthStore = create<AuthState & { profile: Profile | null; csrf: string }>((set, get) => ({
  ...loadPersisted(),
  async login(email, password) {
    const res = (await http.post('/auth/login', { email, password })) as SessionResp
    const next = { profile: res?.profile ?? null, csrf: get().csrf }
    set(next)
    persist(next)
  },
  async register(email, password, name) {
    await http.post('/auth/register', { email, password, name })
  },
  async forgot(email) {
    await http.post('/auth/forgot', { email })
  },
  async reset(token, password) {
    await http.post('/auth/reset', { token, password })
  },
  async logout() {
    try {
      // 后端 logout 已幂等（公开路由、清 cookie、204），过期会话也能安全调用
      await http.post('/auth/logout', {})
    } catch {
      /* session already gone */
    }
    get().clearLocal()
  },
  // 只清本地状态、不发请求：供 401 拦截器使用，避免再触发 API 请求
  clearLocal() {
    const next = { profile: null, csrf: '' }
    set(next)
    persist(next)
  },
  async fetchMe() {
    const profile = (await http.get('/me')) as Profile
    const next = { profile, csrf: get().csrf }
    set(next)
    persist(next)
  },
}))
